import { apiEmployeeCompensationPackages } from "@/api/employees/employee-compensation-package.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeesKeys } from "@/queries/employees/use-employees.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiEmployeeCompensationPerTypes,
  CompensationPackageSave,
} from "@/types/employees/employee-compensation.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const employeeCompensationsKeys = createQueryKeys("employeeCompensations", {
  search: (employeeId: number, pageNumber: number) => ({
    queryKey: [employeeId, pageNumber],
    queryFn: async () => {
      const result = await apiEmployeeCompensationPackages.search(employeeId, pageNumber);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  current: (employeeId: number) => ({
    queryKey: [employeeId],
    queryFn: async () => {
      const result = await apiEmployeeCompensationPackages.getCurrent(employeeId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  summary: (employeeId: number, perType: ApiEmployeeCompensationPerTypes) => ({
    queryKey: [employeeId, perType],
    queryFn: async () => {
      const result = await apiEmployeeCompensationPackages.getSummary(employeeId, perType);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useEmployeeCompensationsSearch = (
  employeeId: number,
  page: number,
  enabled: boolean = true
) => {
  return useQuery({
    ...employeeCompensationsKeys.search(employeeId, page),
    enabled: enabled,
  });
};

export const useEmployeeCompensationCurrent = (employeeId: number, enabled = true) =>
  useQuery({ ...employeeCompensationsKeys.current(employeeId), enabled });

export const useEmployeeCompensationSummary = (
  employeeId: number,
  perType: ApiEmployeeCompensationPerTypes,
  enabled = true
) => useQuery({ ...employeeCompensationsKeys.summary(employeeId, perType), enabled });

export const useEmployeeCompensationSave = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({
      employeeCompensationId,
      payload,
    }: {
      employeeId: number;
      employeeCompensationId: number | undefined;
      payload: CompensationPackageSave;
    }) => {
      const result = !employeeCompensationId
        ? await apiEmployeeCompensationPackages.create(payload)
        : await apiEmployeeCompensationPackages.update(employeeCompensationId, payload);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: employeeCompensationsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeeCompensationsKeys.current(variables.employeeId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: employeeCompensationsKeys.summary._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(variables.employeeId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeCompensationDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      employeeCompensationId,
    }: {
      employeeId: number;
      employeeCompensationId: number;
    }) => {
      const result = await apiEmployeeCompensationPackages.remove(employeeCompensationId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: employeeCompensationsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeeCompensationsKeys.current(variables.employeeId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: employeeCompensationsKeys.summary._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(variables.employeeId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmployeeCompensationPreview = () => {
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({
      payload,
      signal,
    }: {
      payload: CompensationPackageSave;
      signal?: AbortSignal;
    }) => {
      const result = await apiEmployeeCompensationPackages.preview(payload, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => onError(error),
  });
};
